import type { ProjectDtoOutput } from '@batiflow/api-client';
import { ConfirmDialog, EmptyState, SkeletonList, isolate } from '@batiflow/ui';
import { Button, __css, tokens } from '@fluentui/react-components';
import {
  Delete20Regular,
  DocumentArrowRight20Regular,
  Folder24Regular,
} from '@fluentui/react-icons';
import { Suspense, lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShareLinkDialog, SpaceShareLinks } from '@/features/share-links';
import { useTransmittalsView } from '@/features/transmittals/use-transmittals-view';
import { useNotify } from '@/shared/hooks/use-notify';
import { useDeleteFolder, useFolder, useFolderChildren, useRefreshFolders } from './api';
import { DocumentPanel } from './document-panel';
import { useRefreshDocuments } from './documents-api';
import { FolderContent } from './folder-content';
import type { FolderAction } from './folder-menu';
import { FolderTree } from './folder-tree';
import { MoveDialog } from './move-dialog';
import { NameDialog } from './name-dialog';
import { PermissionsDialog } from './permissions-dialog';
import { useUploads } from './use-uploads';
import {
  canManage,
  folderErrorMessage,
  isStaleVersion,
  isStepUpRequired,
  type Folder,
} from './model';

// The trash view is its own chunk, loaded when it is first opened (GED-FE-07).
const TrashPanel = lazy(() =>
  import('./trash-panel').then((module) => ({ default: module.TrashPanel })),
);

// The transmittals list is its own chunk too (GEDX-FE-02).
const TransmittalsPanel = lazy(() =>
  import('@/features/transmittals/transmittals-panel').then((module) => ({
    default: module.TransmittalsPanel,
  })),
);

const useStyles = __css({"side":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Bt984gj":"f6jr5hl","Belr9w4":"f1wn3y0l","Bgto7am":"fuzgk9b"},"root":{"mc9l5x":"f13qh94s","Budl1dq":"f164pvm4","Bt984gj":"f1s2louj","i8kkvl":"f3lra4y","Belr9w4":"f1noh358","Bidxu7u":"fe1az78"},"tree":{"B7ck84d":"f1ewtqcl","Bgto7am":"fuzgk9b","mlrwpw":"fd7hlh9","Bmxbyg5":"f5zp4f","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv","De3pzq":"fxugw4r","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f1o0qvyv","E5pizo":"f1whvlc6","gggo0z":"fi15kbk","Behc1zw":"f10zy6vx","B4m11q7":"f32hy0g","tzxy30":"fgmlvap","B834pou":"f1taw3pw","uk9mye":"f1p2dij1","rm1204":"fwyddsq","Bwezv66":"fwl1lcr","F1poyb":"foi3q75","mbw9mh":"f1a566p","ijz38e":"f1ftuoky","Ctt3hk":"f1crbv9w"}});

type DialogState =
  { kind: 'create'; parent: Folder | null } | { kind: FolderAction; folder: Folder };

export interface FilesTabProps {
  orgId: string;
  /** The project: its internal space holds the folders. */
  project: Pick<ProjectDtoOutput, 'id' | 'code' | 'internalSpaceId' | '_permissions'>;
}

/**
 * The Files tab of a project: the folder tree, the open folder with its breadcrumbs and actions,
 * the permissions dialog (GED-FE-01), the upload zone (GED-FE-02), and the files list with the
 * metadata form and the document panel (GED-FE-03).
 */
export function FilesTab({ orgId, project }: FilesTabProps) {
  const { t } = useTranslation('documents');
  if (!project.internalSpaceId) {
    return (
      <EmptyState
        illustration={<Folder24Regular />}
        title={t('noSpace.title')}
        description={t('noSpace.description')}
      />
    );
  }
  return (
    <FilesWorkspace
      orgId={orgId}
      projectId={project.id}
      projectCode={project.code}
      spaceId={project.internalSpaceId}
      projectManager={project._permissions.includes('can_manage')}
    />
  );
}

export interface FilesWorkspaceProps {
  /**
   * The organisation of the reader. Null in another organisation's space: nothing of the
   * organisation level is read there (naming pattern, member pickers), so the folder permissions
   * and the approval requests, which need them, are not offered.
   */
  orgId: string | null;
  /** The project the space belongs to, when the reader can open it (links to the viewer). */
  projectId: string | null;
  /** The project's code (the `{PROJECT}` token of the naming pattern). */
  projectCode: string | null;
  spaceId: string;
  /** The reader manages the space (or the project): a folder may be created at the root. */
  projectManager: boolean;
}

/**
 * The folder workspace of a space: tree, open folder, uploads and the document panel. Used by the
 * Files tab of a project (its internal space) and by the Documents tab of a shared space.
 */
export function FilesWorkspace({
  orgId,
  projectId,
  projectCode,
  spaceId,
  projectManager,
}: FilesWorkspaceProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const notify = useNotify();
  const refresh = useRefreshFolders(spaceId);
  const refreshDocuments = useRefreshDocuments(spaceId);
  const remove = useDeleteFolder(spaceId);
  // The uploads live with the tab, so a file keeps going while another folder is open. A file
  // shows in the lists as soon as the server has it (being checked) and again once it is settled.
  const uploads = useUploads({
    onCompleted: () => {
      void refreshDocuments();
    },
    onAvailable: () => {
      void refresh();
      void refreshDocuments();
    },
    onRejected: () => {
      void refreshDocuments();
    },
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [openItems, setOpenItems] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [trashOpen, setTrashOpen] = useState(false);
  // The transmittals list replaces the folder content; it is kept in the address.
  const transmittals = useTransmittalsView();

  const roots = useFolderChildren(spaceId, null);
  const open = useFolder(spaceId, selectedId);
  // Creating at the root is for the managers: the project's, or whoever manages a root folder.
  const canManageRoot = projectManager || (roots.data?.items.some(canManage) ?? false);

  const openFolder = (id: string | null, ancestors: readonly string[]) => {
    setSelectedId(id);
    setDocumentId(null);
    if (ancestors.length > 0) setOpenItems((current) => new Set([...current, ...ancestors]));
  };
  const close = () => {
    setDialog(null);
  };

  const afterDelete = (folder: Folder) => {
    const inside =
      selectedId === folder.id ||
      (open.data?.breadcrumbs.some((crumb) => crumb.id === folder.id) ?? false);
    if (inside) setSelectedId(folder.parentId);
  };

  return (
    <div className={styles.root}>
      <div className={styles.side}>
        <div className={styles.tree}>
          <FolderTree
            spaceId={spaceId}
            selectedId={selectedId}
            openItems={openItems}
            onOpenChange={setOpenItems}
            onSelect={(id) => {
              setSelectedId(id);
              setDocumentId(null);
              setTrashOpen(false);
              if (transmittals.open) transmittals.show(false);
            }}
          />
        </div>
        <Button
          appearance="subtle"
          icon={<Delete20Regular />}
          aria-pressed={trashOpen}
          onClick={() => {
            setDocumentId(null);
            if (transmittals.open) transmittals.show(false);
            setTrashOpen((open) => !open);
          }}
        >
          {t('trash.open')}
        </Button>
        <Button
          appearance="subtle"
          icon={<DocumentArrowRight20Regular />}
          aria-pressed={transmittals.open}
          onClick={() => {
            setDocumentId(null);
            setTrashOpen(false);
            transmittals.show(!transmittals.open);
          }}
        >
          {t('transmittals:open')}
        </Button>
        <SpaceShareLinks spaceId={spaceId} canManage={projectManager} />
      </div>
      {trashOpen ? (
        <Suspense fallback={<SkeletonList count={4} />}>
          <TrashPanel
            spaceId={spaceId}
            onClose={() => {
              setTrashOpen(false);
            }}
          />
        </Suspense>
      ) : transmittals.open ? (
        <Suspense fallback={<SkeletonList count={4} />}>
          <TransmittalsPanel
            spaceId={spaceId}
            canSend={projectManager}
            transmittalId={transmittals.transmittalId}
            onOpenTransmittal={(id) => {
              transmittals.show(true, id);
            }}
            onClose={() => {
              transmittals.show(false);
            }}
          />
        </Suspense>
      ) : (
        <FolderContent
          orgId={orgId}
          spaceId={spaceId}
          projectCode={projectCode}
          folderId={selectedId}
          canManageRoot={canManageRoot}
          uploads={uploads}
          canSetPermissions={orgId !== null}
          canTransmit={projectManager}
          onOpen={openFolder}
          onCreate={(parent) => {
            setDialog({ kind: 'create', parent });
          }}
          onAction={(action, folder) => {
            setDialog({ kind: action, folder });
          }}
          onOpenDocument={setDocumentId}
          onDocumentsGone={(ids) => {
            if (documentId && ids.includes(documentId)) setDocumentId(null);
          }}
        />
      )}
      <DocumentPanel
        orgId={orgId}
        projectId={projectId}
        spaceId={spaceId}
        documentId={documentId}
        uploads={uploads}
        canTransmit={projectManager}
        onClose={() => {
          setDocumentId(null);
        }}
      />

      {dialog?.kind === 'create' || dialog?.kind === 'rename' ? (
        <NameDialog
          spaceId={spaceId}
          target={
            dialog.kind === 'create'
              ? { kind: 'create', parent: dialog.parent }
              : { kind: 'rename', folder: dialog.folder }
          }
          onClose={close}
          onCreated={() => {
            if (dialog.kind === 'create' && dialog.parent)
              openFolder(selectedId, [dialog.parent.id]);
          }}
        />
      ) : null}
      {dialog?.kind === 'move' ? (
        <MoveDialog
          spaceId={spaceId}
          folder={dialog.folder}
          canManageRoot={canManageRoot}
          onClose={close}
        />
      ) : null}
      {dialog?.kind === 'permissions' && orgId !== null ? (
        <PermissionsDialog orgId={orgId} spaceId={spaceId} folder={dialog.folder} onClose={close} />
      ) : null}
      {dialog?.kind === 'share' ? (
        <ShareLinkDialog
          spaceId={spaceId}
          target={{ type: 'folder', id: dialog.folder.id, name: dialog.folder.name }}
          showExisting
          onClose={close}
        />
      ) : null}
      {dialog?.kind === 'delete' ? (
        <ConfirmDialog
          open
          destructive
          title={t('delete.title', { name: isolate(dialog.folder.name) })}
          body={t('delete.body')}
          confirmLabel={t('delete.confirm')}
          cancelLabel={t('common:actions.cancel')}
          onOpenChange={(next) => {
            if (!next) close();
          }}
          onConfirm={async () => {
            const { folder } = dialog;
            await remove.mutateAsync({ folderId: folder.id, version: folder.version });
            notify({
              intent: 'success',
              message: t('delete.done', { name: isolate(folder.name) }),
            });
            afterDelete(folder);
            await refresh();
          }}
          onError={(error) => {
            if (isStepUpRequired(error)) return;
            notify({
              intent: isStaleVersion(error) ? 'warning' : 'error',
              message: folderErrorMessage(t, error),
            });
            if (isStaleVersion(error)) {
              void refresh();
              close();
            }
          }}
        />
      ) : null}
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/files-tab.griffel.css";